import { FunctionComponent, useState, useEffect, SetStateAction } from "react";
import TextOptionBoxes from "../TextOptionBox/TextOptionBoxes";
import OptionBoxes from "../OptionBox/OptionBoxes";
import IconOptionBoxes from "../IconOptionBox/IconOptionBoxes";
import TextFields from "../TextField/TextFields";
import OptIn from "../OptIn/OptIn";
import "dayjs/locale/de";
import { ValidateResult, ValidateResultInline, ValidateResultPopup, isValidateResultInline, validate } from "../../../lib/validations/validate";
import { AnswerChoiceValue, FormData, toPlainValue } from "../form-data";
import { AnswerId, answerIdFromComponentId, PlainAnswerChoice, UnencryptedAnswers } from "../../../lib/types/form/submission/answer";
import { ComponentId, PageId } from "../../../lib/types/ids";
import { addToScore, createScore, Score } from "../../../lib/types/form/submission/score";
import { Dispatch } from "react";
import { ComponentValidations as ValidateComponentValidations } from "../../../lib/validations/component-validations";
import { Page } from "../../../lib/ssi/form.types";
import { IsPlain } from "../../../lib/types/crypto";
import { UnexpectedValueError } from "../../../lib/unexpected-value-error";
import { UnreachableVariantError } from "../../../lib/unreachable-variant-error";
import Button from "../Button/Button";
import { isEmpty } from "fp-ts/lib/Record";

type PageWrapperProps = {
  className?: string;
  data: Page;
  index: number;
  activeIndex: number;
  activePage: PageId;
  nextPage: PageId | undefined;
  previousPage: PageId | undefined;
  resolveExpression: (expression: string) => unknown;
  setActivePage: (page: PageId) => void;
  setAnswers: (answers: UnencryptedAnswers) => void;
  setAnswersAndPublish: (answers: UnencryptedAnswers) => Promise<void>;
  setScore: Dispatch<SetStateAction<{ [key: PageId]: Score }>>;
  answers: UnencryptedAnswers;
  score: { [key: PageId]: Score };
  scoreExpression: string | undefined;
  isLoading: boolean;
};

const getValidationsFromComponent = (component: Page["components"][0]) => {
  if (component.componentType === "input" && component.validations) {
    return component.validations;
  }

  return null;
};

function resolveValidations(
  resolveExpression: (expression: string) => unknown,
  validations: Partial<Record<keyof ValidateComponentValidations, boolean | number | undefined | string | string[]>>): ValidateComponentValidations {
  const resolvedValidations: ValidateComponentValidations = {};
  for (const key of Object.keys(validations)) {
    const value = validations[key];
    const resolvedValue =
      typeof value === "string"
        ? resolveExpression(value)
        : value;

    if (key === "isAge") {
      resolvedValidations[key] = typeof resolvedValue === "number" ? resolvedValue : undefined;
    } else if(key === "ibanStartsWith") {
      resolvedValidations[key] = 
        typeof resolvedValue === "string" ? [resolvedValue] 
        : Array.isArray(resolvedValue) ? resolvedValue.flatMap(x => typeof x === "string" ? [x] : [])
        : undefined;
    } else {
      resolvedValidations[key] = !!resolvedValue;
    }
  }

  return resolvedValidations;
}

const PageWrapper: FunctionComponent<PageWrapperProps> = ({
  data,
  index,
  activeIndex,
  activePage,
  nextPage,
  previousPage,
  resolveExpression,
  setActivePage,
  setAnswers,
  setAnswersAndPublish,
  setScore,
  answers,
  scoreExpression,
  score,
  isLoading,
}) => {
  const formPageErrorsPopupId = `formPageErrorsPopup${index}`;
  const [transition, setTransition] = useState(
    activePage !== data.id ? "hidden" : "block"
  );
  const [formErrors, setFormErrors] = useState<Record<string, ValidateResultInline>>({});
  const [formPopups, setFormPopups] = useState<Record<string, ValidateResultPopup>>({});
  const [formReadPopups, setFormReadPopoups] = useState<Record<string, boolean | undefined>>({});
  const [storedFormData, setStoredFormData] = useState<FormData | undefined>();
  // Slide Animation bei Pagewechsel
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (activePage === data.id) {
      if (transition !== "block") {
        timeout = setTimeout(() => setTransition("block opacity-100"), 300);
      }
    } else {
      if (transition !== "hidden") {
        setTransition(
          `${index - activeIndex > 0 ? "" : "-"}translate-x-full opacity-0`
        );
        timeout = setTimeout(() => setTransition("hidden"), 300);
      }
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [activePage !== data.id]);

  useEffect(() => {
    setFormErrors({});
    setFormPopups({});
  }, [activeIndex]);

  useEffect(() => {
    const popup = document.getElementById(formPageErrorsPopupId) as HTMLDialogElement;
    if (popup && Object.values(formPopups).length > 0) {
      popup.showModal();
    }
  }, [formPageErrorsPopupId, formPopups]);

  // Check, ob alle Daten korrekt eingegeben wurden
  // Anschließend werden die Daten weitergegeben
  const onDataSelected = async (formData?: FormData) => {
    if (formData && storedFormData) {
      // Felder die sich seit dem letzten Mal geändert haben, müssen
      // aus dem "gelesene Popups"-Record entfernt werden.
      const oldValues: Record<string, AnswerChoiceValue> = {};
      for (const [key, item] of Object.entries(storedFormData.choices)) {
        oldValues[key] = item.value;
      }
      let formReadPopupsChanged = false;
      for (const [key, item] of Object.entries(formData.choices)) {
        if (item.value !== oldValues[key]) {
          delete formReadPopups[key];
          formReadPopupsChanged = true;
        }
      }
      if (formReadPopupsChanged) {
        setFormReadPopoups(formReadPopups);
      }
    }

    formData ??= storedFormData;
    if (!formData) return;
    setStoredFormData(formData);

    let errors: { [key: string]: ValidateResult } = {};
    // Check Page Validations – if error, set form error
    const hasData = formData.choices && Object.keys(formData.choices).length > 0;
    if (data.validations.required) {
      if (!hasData) {
        errors.general = { type: "inline", text: "Ups... Du hast vergessen etwas auszuwählen" };
        // If page is multiple choice return – Else continue to check and display each inputs' validations
        if (formData.type !== "multiple_choice") {
          return;
        }
      } else {
        delete errors.general;
      }
    }
    // Check each component validations – If error, set component error
    if (hasData) {
      const validations: { [key: ComponentId]: ValidateComponentValidations } = {};
      let hasValidations = false;

      data.components.forEach((el) => {
        const elValidations = getValidationsFromComponent(el);
        if (elValidations) {
          hasValidations = true;
          validations[el.id] = resolveValidations(resolveExpression, elValidations);
        }
      });
      if (hasValidations) {
        errors = validate(formData.choices, validations);
      }
    }
    // If error set errors else continue
    const onlyInlineFormErrors: Record<string, ValidateResultInline> = {};
    const onlyPopupFormErrors: Record<string, ValidateResultPopup> = {};
    for (const key of Object.keys(errors)) {
      const error = errors[key] as typeof errors[typeof key];
      if (isValidateResultInline(error)) {
        onlyInlineFormErrors[key] = error;
      } else {
        if (!formReadPopups[key]) {
          onlyPopupFormErrors[key] = error;
        }
      }
    }

    setFormErrors(onlyInlineFormErrors);
    setFormPopups(onlyPopupFormErrors);
    if (!isEmpty(onlyInlineFormErrors) || !isEmpty(onlyPopupFormErrors)) {
      return;
    }

    // If addToScore is true, add answer value to score
    if (data.addToScore) {
      let scoreToAdd = createScore(0);
      Object.values(formData.choices).forEach((el) => {
        if (typeof el?.value === "number") {
          scoreToAdd = addToScore(scoreToAdd, createScore(el.value));
        }
      });
      setScore((current: { [key: PageId]: Score }) => ({
        ...current,
        [data.id]: scoreToAdd,
      }));
    }
    // Set answers and check if you should publish
    const answerDataChoices: { [key: AnswerId]: PlainAnswerChoice } = {};
    for (const id of Object.keys(formData.choices)) {
      const choice = formData.choices[id];
      const answerId = answerIdFromComponentId(id);
      if (!choice?.label)
        continue;

      answerDataChoices[answerId] = {
        id: answerId,
        label: choice.label,
        value: toPlainValue(choice.value),
      };
    }

    const answerData: UnencryptedAnswers = {
      [data.id]: {
        data: answerDataChoices,
        page: formData.page,
        title: data.heading,
        description: data.description,
        type: formData.type,
        encrypt: !!data.encrypt,
        plain: data.encrypt ? true : UnexpectedValueError.throwIfLeft("answerData", IsPlain.decode(true)),
      },
    };
    // Wenn es die letzte Seite ist, wird die Anfrage abgeschickt
    if (data.type === "opt-in" || !nextPage) {
      await setAnswersAndPublish(answerData);
    } else {
      setAnswers(answerData);
      setActivePage(nextPage);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (isEmpty(formReadPopups)) return;

    // Wenn alle Popups gelesen wurden, versuche automatisch erneut weiter zu gehen
    onDataSelected();
  }, [formReadPopups]);

  const onBackButtonPressed = () => {
    if (!previousPage) return;

    setActivePage(previousPage);
  };

  let content;
  if (data.type === "text_multiple_choice") {
    content = (
      <TextOptionBoxes
        data={data}
        nextPage={nextPage}
        onDataSelected={e => { onDataSelected(e); }}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        resolveExpression={resolveExpression}
        errors={formErrors}
        isActive={activeIndex === index}
        isLoading={isLoading}
      />
    );
  } else if (data.type === "list_single_choice") {
    content = (
      <OptionBoxes
        data={data}
        nextPage={nextPage}
        onDataSelected={e => { onDataSelected(e); }}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        resolveExpression={resolveExpression}
        errors={formErrors}
        isLoading={isLoading}
      />
    );
  } else if (data.type === "icon_multiple_choice") {
    content = (
      <IconOptionBoxes
        data={data}
        nextPage={nextPage}
        onDataSelected={e => { onDataSelected(e); }}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        resolveExpression={resolveExpression}
        errors={formErrors}
        isActive={activeIndex === index}
        isLoading={isLoading}
      />
    );
  } else if (data.type === "input") {
    content = (
      <TextFields
        data={data}
        nextPage={nextPage}
        onDataSelected={e => { onDataSelected(e); }}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        resolveExpression={resolveExpression}
        errors={formErrors}
        isActive={activeIndex === index}
        isLoading={isLoading}
      />
    );
  } else if (data.type === "opt-in") {
    //console.log("scoreExpression", scoreExpression);
    content = (
      <OptIn
        data={data}
        onDataSelected={e => { onDataSelected(e); }}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        resolveExpression={resolveExpression}
        errors={formErrors}
        answers={answers}
        scoreExpression={scoreExpression}
        score={score}
        isActive={activeIndex === index}
        isLoading={isLoading}
      />
    );
  }
  else {
    throw new UnreachableVariantError(data);
  }
  const notes = (data.notes ?? []).map((el: string, index: number) => {
    return <p className="text-xs text-gray-500 mb-4" key={index}>{el}</p>;
  });
  const popup = (function () {
    const elements = Object.values(formPopups).map(item =>
      <div className="mt-8 max-w-xl">{item.text}</div>
    );


    function closeDialog() {
      const popup = document.getElementById(formPageErrorsPopupId) as HTMLDialogElement;
      if (popup && popup.open) {
        popup.close();
      }

      const read = { ...formReadPopups };
      for (const key in formPopups) {
        read[key] = true;
        delete formPopups[key];
      }
      setFormReadPopoups(read);
    }

    return [
      <dialog className="dialog" id={formPageErrorsPopupId}>
        {elements}
        <div className="flex justify-center mt-4">
          {<Button variant="primary" onPress={closeDialog}>OK</Button>}
        </div>
      </dialog>
    ];
  })();
  return (
    <div
      className={`w-full flex flex-col max-w-5xl mx-auto items-center duration-300 transform ${transition} page-wrapper`}
      data-id={PageId.encode(data.id)}
      data-type={data.type}
    >
      {content}
      <div className="mt-8 max-w-xl">{notes}</div>
      {popup}
    </div>
  );
};

export default PageWrapper;
